import { createContext, useContext } from "react";
import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { isApiError } from "../api";

export const CsrfContext = createContext<() => string | null>(() => null);

export function useCsrf(): () => string | null {
  return useContext(CsrfContext);
}

export interface CommandOptions<T, V> {
  readonly run: (csrf: string | null, variables: V) => Promise<T>;
  readonly invalidate: readonly QueryKey[];
  readonly invalidateOnConflict?: readonly QueryKey[];
  readonly onSuccess?: (result: T, variables: V) => void;
}

export function useCommandMutation<T, V = void>(options: CommandOptions<T, V>) {
  const client = useQueryClient();
  const csrf = useCsrf();
  const refresh = (keys: readonly QueryKey[]) =>
    Promise.all(keys.map((queryKey) => client.invalidateQueries({ queryKey })));
  return useMutation<T, Error, V>({
    mutationFn: (variables) => options.run(csrf(), variables),
    onSuccess: async (result, variables) => {
      await refresh(options.invalidate);
      options.onSuccess?.(result, variables);
    },
    onError: async (error) => {
      if (options.invalidateOnConflict && isApiError(error) && error.code === "conflict")
        await refresh(options.invalidateOnConflict);
    },
  });
}
